import { store } from '@/app/store';
import { selectPortfolioBalanceById, useGetPortfolioBalancesQuery } from '@/features/balance/balancesApi';
import { useGetMarketInfoQuery } from '@/features/marketInfo/marketInfoApi';
import { useGetUserQuery } from '@/features/user/userApi';
import PageWrapper from '@/shared/components/PageWrapper/PageWrapper';
import TradingBlocker from '@/shared/components/TradingBlocker/TradingBlocker';
import { IonGrid, IonRow, useIonToast } from '@ionic/react';
import { withTranslation } from 'react-i18next';
import { selectIsLoggedIn } from '@/features/oAuth/oAuthSlice';
import GenericErrorComponent from '@/shared/components/GenericErrorComponent/GenericErrorComponent';
import LoadingSpinner from '@/shared/components/LoadingSpinner/LoadingSpinner';
import { useSelectedAssetId } from '@/shared/hooks/assets';
import { useIsMobile } from '@/shared/hooks/devices';
import { useUser, userCompletedKyc } from '@/shared/hooks/user';
import { useDispatch, useSelector } from 'react-redux';
import WalletDetail from './WalletDetail';
import WalletList from './WalletList';
import { cryptoApi, useGetWithdrawCryptoChallengeMutation } from '@/features/crypto/cryptoApi';
import { useCallback, useRef, useState } from 'react';
import { TravelRuleType } from '@/types/crypto';

const Wallet: React.FC = () => {
  const [initiateTravelRule, setInitiateTravelRule] = useState(false);
  const [initiateDepositTravelRule, setInitiateDepositTravelRule] = useState(false);

  const [withdrawCryptoData, setWithdrawCryptoData] = useState<any>({
    id: null,
    intentId: null
  });
  const [depositCryptoData, setDepositCryptoData] = useState<any>({
    id: null,
    intentId: null
  });

  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const modal = useRef<HTMLIonModalElement>(null);

  const isMobile = useIsMobile();
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const { error: marketInfoError } = useGetMarketInfoQuery(undefined);

  const { error: portfolioBalancesError } = useGetPortfolioBalancesQuery(undefined, {
    skip: !isLoggedIn
  });

  const { isLoading: isUserDataLoading, error: userError } = useGetUserQuery(undefined, {
    skip: !isLoggedIn
  });

  const user = useUser();
  const [getWithdrawCryptoChallenge, { data: withdrawCryptoChallengeData, isLoading, error }] =
    useGetWithdrawCryptoChallengeMutation();
  const selectedAssetId = useSelectedAssetId();
  const asset = selectPortfolioBalanceById(store.getState(), selectedAssetId || '');
  const showListView = (!asset && isMobile) || !isMobile;
  const showDetailView = !!asset || !isMobile;
  const userIsKyced = user ? userCompletedKyc(user) : false;

  const isServerError = () => {
    if (
      (portfolioBalancesError && 'status' in portfolioBalancesError && portfolioBalancesError.status === 500) ||
      (userError && 'status' in userError && userError.status === 500) ||
      (marketInfoError && 'status' in marketInfoError && marketInfoError.status === 500)
    ) {
      return true;
    }
  };

  const handleInitTravelRule = useCallback(
    (value: boolean, cryptoData: any, type: string = TravelRuleType.Withdrawal) => {
      if (type === TravelRuleType.Withdrawal) {
        setInitiateTravelRule(value);
        setWithdrawCryptoData(cryptoData);
      } else if (type === TravelRuleType.Deposit) {
        setInitiateDepositTravelRule(value);
        setDepositCryptoData(cryptoData);
      }
    },
    []
  );

  const handleChallenge = useCallback(async () => {
    try {
      await getWithdrawCryptoChallenge(withdrawCryptoData?.id).unwrap();
      handleInitTravelRule(false, {
        id: withdrawCryptoData?.id,
        intentId: withdrawCryptoData?.intentId
      });
      setIsOpen(true);
    } catch (error) {
      setIsOpen(false);
      console.error(error);
      handleInitTravelRule(false, {
        id: null,
        intentId: null
      });
    }
  }, [withdrawCryptoData, getWithdrawCryptoChallenge, handleInitTravelRule]);

  const refetchTravelRuleDeposits = useCallback(() => {
    dispatch(cryptoApi.util.invalidateTags(['CryptoDeposits']));
  }, [dispatch]);

  const handleSuccessfulDeposits = useCallback(() => {
    try {
      refetchTravelRuleDeposits();
      handleInitTravelRule(
        false,
        {
          id: null,
          intentId: null
        },
        TravelRuleType.Deposit
      );
    } catch (error) {
      console.error(error);
      handleInitTravelRule(
        false,
        {
          id: null,
          intentId: null
        },
        TravelRuleType.Deposit
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawCryptoData]);

  return (
    <PageWrapper name="wallet-page" mainPage={isMobile && !showDetailView}>
      {!isUserDataLoading ? (
        <>
          <IonGrid>
            <IonRow>
              {showListView && <WalletList handleInitTravelRule={handleInitTravelRule} />}
              {showDetailView && (
                <WalletDetail
                  initiateTravelRule={initiateTravelRule}
                  initiateDepositTravelRule={initiateDepositTravelRule}
                  withdrawCryptoData={withdrawCryptoData}
                  depositCryptoData={depositCryptoData}
                  withdrawCryptoChallengeData={withdrawCryptoChallengeData}
                  isOpen={isOpen}
                  modal={modal}
                  setIsOpen={setIsOpen}
                  handleInitTravelRule={handleInitTravelRule}
                  handleChallenge={handleChallenge}
                  handleSuccessfulDeposits={handleSuccessfulDeposits}
                />
              )}
            </IonRow>
          </IonGrid>
          {!userIsKyced && isLoggedIn && <TradingBlocker />}
          {isServerError() && <GenericErrorComponent />}
        </>
      ) : (
        <LoadingSpinner />
      )}
    </PageWrapper>
  );
};

export default withTranslation()(Wallet);
